import { useAuth0, User } from "@auth0/auth0-react";
import { useQuery } from "@tanstack/react-query";
import { tractorsTableOptions, useDeleteTractor } from "./api";
import type { Tractor } from "./api";
import Button from "components/button";
import { useMemo, useState } from "react";
import { AiFillCaretDown } from "react-icons/ai";
import { MdDelete, MdEdit } from "react-icons/md";
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import ColumnFilter from "components/column-filter";
import ModalForm from "./modal-form";

// The shape of the tractor create/edit form's local state. Numeric fields are
// held as strings while the user types (text inputs), then coerced to numbers on
// submit (see modal-form.tsx). Shared with the modal so both sides agree on it.
export interface TractorFormData {
  id: number | null;
  unit_number: string;
  email_address: string;
  tank_capacity: number | string;
  MPG: number | string;
  status: boolean;
}

// MPG must be present here or the create form's MPG input renders uncontrolled
// and submits Number(undefined) === NaN.
const INIT_TRACTOR_DATA: TractorFormData = {
  id: null,
  unit_number: "",
  email_address: "",
  tank_capacity: "",
  MPG: "",
  status: false,
};

export default function TractorsPage() {
  const auth = useAuth0();
  const user = auth.user as Exclude<User, undefined>;
  const customerCode = user["https://ifuelsmart.com/company"] as string;
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [tractorData, setTractorData] = useState<TractorFormData>(INIT_TRACTOR_DATA);

  const { data: tractors = [] } = useQuery(tractorsTableOptions(customerCode));
  const deleteTractorMutation = useDeleteTractor();

  const data = useMemo(() => [...tractors], [tractors]);

  const columns = useMemo<ColumnDef<Tractor>[]>(
    () => [
      { header: "Id", accessorKey: "id", enableColumnFilter: false },
      { header: "Unit Number", accessorKey: "unit_number", meta: { filterComponent: ColumnFilter } },
      { header: "Email Address / Driver Id", accessorKey: "email_address", enableColumnFilter: false },
      { header: "Tank Capactiy", accessorKey: "tank_capacity", enableColumnFilter: false },
      { header: "MPG", accessorKey: "MPG", enableColumnFilter: false },
      {
        header: "Status",
        accessorKey: "status",
        enableColumnFilter: false,
        cell: ({ getValue }) =>
          getValue<Tractor["status"]>() ? (
            <span className="p-2 bg-green-100 rounded">Enabled</span>
          ) : (
            <span className="p-2 bg-red-100 rounded">Disabled</span>
          ),
      },
      {
        id: "actions",
        header: "Actions",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }) => {
          function handleEditClick(tractor: Tractor) {
            setTractorData({
              id: tractor.id,
              unit_number: tractor.unit_number,
              email_address: tractor.email_address ?? "",
              tank_capacity: tractor.tank_capacity,
              MPG: tractor.MPG,
              status: Boolean(tractor.status),
            });
            setIsModalOpen(true);
          }

          function handleDeleteClick(tractor: Tractor) {
            const confirmDelete = window.confirm(`Are you sure you want to delete tractor ${tractor.unit_number}`);
            if (confirmDelete && tractor.id != null) {
              deleteTractorMutation.mutate(
                { customer: customerCode, id: tractor.id },
                { onError: () => window.alert("Failed to delete tractor") }
              );
            }
          }

          return (
            <div className="flex space-x-4 text-center">
              <button onClick={() => handleEditClick(row.original)}>
                <MdEdit />
              </button>
              <button onClick={() => handleDeleteClick(row.original)}>
                <MdDelete />
              </button>
            </div>
          );
        },
      },
    ],
    [customerCode, deleteTractorMutation]
  );

  const table = useReactTable({
    data,
    columns,
    initialState: {
      sorting: [{ id: "unit_number", desc: true }],
    },
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  function resetTractorData() {
    setTractorData(INIT_TRACTOR_DATA);
  }

  return (
    <>
      {isModalOpen && (
        <ModalForm
          tractorData={tractorData}
          setTractorData={setTractorData}
          resetTractorData={resetTractorData}
          setIsModalOpen={setIsModalOpen}
        />
      )}
      <div className="h-full flex flex-col p-4">
        {/* Actions */}
        <div className="flex justify-end mb-4">
          <Button type="button" className="!w-max" onClick={() => setIsModalOpen(true)}>
            Add New Tractor
          </Button>
        </div>
        {/* Table */}
        <div className="flex-1 relative">
          <div className="absolute inset-0 overflow-auto">
            <div className="min-w-full">
              <div className="shadow overflow-hidden border-b border-gray-200 sm:rounded-lg">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50 sticky top-0">
                    {table.getHeaderGroups().map((headerGroup) => (
                      <tr key={headerGroup.id}>
                        {headerGroup.headers.map((header) => {
                          const column = header.column;
                          const sorted = column.getIsSorted();
                          const Filter = column.columnDef.meta?.filterComponent;
                          // Override the default sort toggle so a click cycles
                          // asc<->desc only (never back to unsorted) and never
                          // multi-sorts.
                          const handleSortClick = () => {
                            if (!column.getCanSort()) return;
                            if (!sorted) {
                              column.toggleSorting(false, false);
                            } else {
                              column.toggleSorting(sorted === "asc", false);
                            }
                          };
                          return (
                          <th
                            key={header.id}
                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50 whitespace-nowrap"
                            onClick={handleSortClick}
                          >
                            {flexRender(column.columnDef.header, header.getContext())}
                            <span>
                              {sorted ? (
                                sorted === "desc" ? (
                                  <AiFillCaretDown className="inline-block text-base" />
                                ) : (
                                  <AiFillCaretDown className="inline-block text-base rotate-180" />
                                )
                              ) : (
                                ""
                              )}
                            </span>
                            <div>{Filter ? <Filter column={column} /> : null}</div>
                          </th>
                          );
                        })}
                      </tr>
                    ))}
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {table.getRowModel().rows.map((row) => (
                      <tr key={row.id}>
                        {row.getVisibleCells().map((cell) => (
                          <td key={cell.id} className="px-6 py-4 whitespace-nowrap">
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
